import { type IReactionDisposer, makeAutoObservable, runInAction, when } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import {
  type IIntegrationKey,
  type Integration,
  isValidIntegrationKey,
  IntegrationKey,
  type IResponseIntegrationRefreshed,
} from 'entities/Integrations'
import { WebsocketEvent } from 'entities/WebSocket'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { SettingsRoutes } from 'pages/settings/route/type'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'

export class IntegrationDetailsStore {
  private readonly _integrationKey: IIntegrationKey | string | null = null
  private _integration: Integration | null = null
  private _isLoading = true
  private _isNotFound = false
  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _whenManageConnectionEndedReaction: IReactionDisposer | null = null
  private _isAircallNumbersImporting = false
  private _aircallNumbersUnsubscribe: (() => void) | null = null

  constructor(integrationKey: string | undefined, private _entities: IEntitiesStore) {
    if (integrationKey && isValidIntegrationKey(integrationKey)) {
      this._integrationKey = integrationKey
    } else {
      this._isNotFound = true
      this._isLoading = false
    }
    makeAutoObservable(this)
  }

  getIntegrationDetails = async () => {
    if (!this._integrationKey) return
    runInAction(() => {
      this._isLoading = true
    })

    try {
      await this._entities.integrationsStore.fetchIntegrations()
      const integration = this._entities.integrationsStore.getIntegration(this._integrationKey)

      runInAction(() => {
        if (!integration) {
          this._isNotFound = true
          return
        }
        this._integration = integration
      })
    } catch (e) {
      logger.error(e)
      runInAction(() => {
        this._isNotFound = true
      })
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  private _redirectToSettings = () => {
    if (this.integration?.isNangoProvider) return
    uiStore.changeRouteViaVue({
      path: `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${this._integrationKey}/${IntegrationsRoutes.settings}`,
    })
  }

  handleEventIntegrationConnect = () => {
    if (!this.integration) return

    if (!this.isManageConnectionProcessing) {
      this._redirectToSettings()
      return
    }

    this._whenManageConnectionEndedReaction = when(
      () => !this.isManageConnectionProcessing,
      () => this._redirectToSettings()
    )
  }

  sendIntegrationLogEvent(integrationKey: string) {
    this._entities.eventLogStore.logEvent(
      `${integrationKey.charAt(0).toUpperCase() + integrationKey.slice(1)} Enabled`,
      {
        event_id: `${integrationKey}_enabled`,
        userId: this._entities.usersStore.user_owner_id || 'anonymous_user',
        agencyId: this._entities.organizationStore.agency_id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  private setupAircallNumbersListener = () => {
    this._isAircallNumbersImporting = true

    this._aircallNumbersUnsubscribe = this._entities.websocket.on(
      WebsocketEvent.IntegrationNumbersRefreshed,
      (data: IResponseIntegrationRefreshed) => {
        if (
          data.integration_user.integration.key === IntegrationKey.aircall &&
          data.user === this._entities.usersStore.user?.id
        ) {
          runInAction(() => {
            this._isAircallNumbersImporting = false
          })

          showToast({
            type: 'success',
            title: 'All Aircall numbers are imported successfully.',
          })

          this._cleanupAircallNumbersListener()
        }
      }
    )
  }

  private _cleanupAircallNumbersListener = () => {
    this._aircallNumbersUnsubscribe?.()
    this._aircallNumbersUnsubscribe = null
  }

  onConnect = async () => {
    if (!this._integration) return

    if (this._integration.key === 'integrately') this.sendIntegrationLogEvent('integrately')
    if (this._integration.key === 'n8n') this.sendIntegrationLogEvent('n8n')

    if (this._integration.key === 'aircall') {
      this.setupAircallNumbersListener()
    }

    await this._manageIntegrationConnectionStore.connect(this._integration)
  }

  onDisconnect = async () => {
    if (!this._integration) return

    await this._manageIntegrationConnectionStore.disconnect(this._integration)
  }

  get isLoading() {
    return this._isLoading
  }

  get isManageConnectionProcessing() {
    return this._manageIntegrationConnectionStore.processing
  }

  get isNotFound() {
    return this._isNotFound
  }

  get integration() {
    return this._integration
  }

  get hasSiblingConnections() {
    return !!this._entities.integrationsStore.connectedIntegrations.length
  }

  get hasConnectionLimit() {
    return (
      (this._entities.subscriptionStore.isTrial || this._entities.subscriptionStore.isPAYGPlan) &&
      this.hasSiblingConnections
    )
  }

  get isAircallNumbersImporting() {
    return this._isAircallNumbersImporting
  }

  dispose = () => {
    this._manageIntegrationConnectionStore.dispose()
    this._whenManageConnectionEndedReaction?.()
    this._cleanupAircallNumbersListener()
  }
}
